// Procedural models of the room objects (CIN-15118), described as plain parts so the 3D model, the
// tests and the selection outline all read the same numbers. Local space: meters, origin at the
// center of the footprint, base at y = 0, x across the width, z along the depth. Which side is the
// "front" depends on the object: the screen surface, the stage lip, the speaker drivers and the foot of
// the stairs face +z (the audience, plan -y); the projector lens looks -z (the screen, plan +y);
// stairs climb toward -z. Rotating the object turns all of that with it. `frontOf` (object-kinds.ts)
// is that choice as data, for the plan-space code that needs a direction.
// Pure module: relative imports only.
import { GROUND_Y, MM } from '../../seat-map-3d/scene-geometry.ts';
import type { Vec3 } from '../../seat-map-3d/scene-geometry.ts';
import type { ObjectProfile, PlanPoint } from '../outline/types.ts';
import { CUPHOLDER_SPACING_RANGE } from '../outline/validate.ts';
import { curveDepthAt, curveEndAngle } from '../screen-curve.ts';

import type { RoomObjectKind } from './object-kinds.ts';
import {
    type LocalPoint,
    offsetRingLocal,
    pathRuns,
    planToLocal,
    pointsAlong,
    profilePlanPoints,
    resampleRun,
    stepModel,
    toLocalRing,
    towardAudience,
    wallRing
} from './profile-geometry.ts';

export type ObjectPartMaterial = 'body' | 'accent' | 'glow' | 'dark' | 'metal' | 'glass' | 'brass';

export type ObjectPart = {
    center: Vec3;
    /** Bounding size in local axes (for a cylinder: its diameter(s) and its length along `axis`). */
    size: Vec3;
    material: ObjectPartMaterial;
    shape?: 'box' | 'cylinder' | 'extrusion';
    /** Cylinders only: the long axis, `y` (upright) when omitted. */
    axis?: 'y' | 'z';
    /**
     * Rotation around x in radians, applied before `rotY`: the sloped stair handrail, and with `rotY` a strut
     * between any two points (see `strut`).
     */
    tilt?: number;
    /** Rotation around the part's own vertical axis in radians, applied after `tilt`. */
    rotY?: number;
    /**
     * Extrusion only: the footprint ring in local meters (x, z) and its span in y (`center.y +- size.y / 2`).
     * `center` and `size` are the ring's bounding box, so bounds and collision work like for any other part.
     */
    outline?: LocalPoint[];
    /** Parts the model renders specially instead of as a plain mesh. */
    tag?: 'screen-surface' | 'exit-sign' | 'rounded';
    /**
     * `screen-surface` of a curved screen (CIN-15402): how far the picture's ends stand in front of its center, m.
     * `center`/`size` stay the flat picture's box, its front face at the center of the arc.
     */
    curve?: number;
};

export type ObjectSize = { width: number; depth: number; height: number };

export const OBJECT_DEFAULTS: Record<
    RoomObjectKind,
    { width: number; depth: number; height: number; z: number; name: string }
> = {
    screen: { width: 10000, depth: 200, height: 4200, z: 900, name: 'screen' },
    stage: { width: 8000, depth: 2000, height: 600, z: 0, name: 'stage' },
    stairs: { width: 1200, depth: 2400, height: 900, z: 0, name: 'stairs' },
    pillar: { width: 500, depth: 500, height: 4000, z: 0, name: 'pillar' },
    wall: { width: 3000, depth: 150, height: 2500, z: 0, name: 'wall' },
    door: { width: 1000, depth: 150, height: 2100, z: 0, name: 'door' },
    // Door leaf (2100) plus the exit sign above it.
    emergency_exit: { width: 1000, depth: 150, height: 2400, z: 0, name: 'emergency-exit' },
    speaker: { width: 700, depth: 500, height: 1200, z: 2500, name: 'speaker' },
    projector: { width: 800, depth: 1000, height: 500, z: 3500, name: 'projector' },
    table: { width: 800, depth: 800, height: 750, z: 0, name: 'table' },
    barrier: { width: 4000, depth: 200, height: 900, z: 0, name: 'barrier' },
    counter: { width: 8000, depth: 500, height: 750, z: 0, name: 'counter' },
    railing: { width: 3000, depth: 60, height: 1000, z: 0, name: 'railing' },
    spotlight: { width: 300, depth: 400, height: 350, z: 3500, name: 'spotlight' },
    truss: { width: 6000, depth: 300, height: 300, z: 4200, name: 'truss' },
    footstool: { width: 450, depth: 400, height: 450, z: 0, name: 'footstool' },
    other: { width: 1000, depth: 1000, height: 1000, z: 0, name: 'other' }
};

/** `emergency_exit` -> `emergency-exit`, the segment used in i18n keys. */
export function objectKindKey(kind: RoomObjectKind): string {
    return kind.replace(/_/g, '-');
}

/** Round parts (pillar, table) stay round for near-square footprints and turn into boxes otherwise. */
export function isRoundFootprint(width: number, depth: number): boolean {
    return Math.abs(width - depth) / Math.max(width, depth, 1) < 0.15;
}

/** Plan placement (mm, plan y toward the screen, rotation clockwise from above) -> world (meters). */
export function objectWorldTransform(o: { x: number; y: number; z: number; rotation: number }): {
    position: Vec3;
    rotationY: number;
} {
    return {
        position: { x: o.x / MM, y: GROUND_Y + o.z / MM, z: -o.y / MM },
        // + 0 turns -0 into 0
        rotationY: -(o.rotation * Math.PI) / 180 + 0
    };
}

const vec = (x: number, y: number, z: number): Vec3 => ({ x, y, z });

/** A part from extents, which reads better than center/size when stacking pieces. */
function span(
    material: ObjectPartMaterial,
    [x0, x1]: [number, number],
    [y0, y1]: [number, number],
    [z0, z1]: [number, number],
    extra: Partial<ObjectPart> = {}
): ObjectPart {
    return {
        center: vec((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2),
        size: vec(x1 - x0, y1 - y0, z1 - z0),
        material,
        ...extra
    };
}

function cylinder(
    material: ObjectPartMaterial,
    center: Vec3,
    diameter: { x: number; z: number },
    height: number,
    axis: 'y' | 'z' = 'y'
): ObjectPart {
    const size = axis === 'y' ? vec(diameter.x, height, diameter.z) : vec(diameter.x, diameter.z, height);
    return { center, size, material, shape: 'cylinder', axis: axis === 'y' ? undefined : axis };
}

/** A box between two local points, `thickness` square: any direction (pitch by `tilt`, heading by `rotY`). */
function strut(material: ObjectPartMaterial, a: Vec3, b: Vec3, thickness: number, thicknessY = thickness): ObjectPart {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;
    const flat = Math.hypot(dx, dz);
    return {
        center: vec((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2),
        size: vec(thickness, thicknessY, Math.hypot(flat, dy)),
        material,
        tilt: -Math.atan2(dy, flat) || 0,
        rotY: Math.atan2(dx, dz) || 0
    };
}

/** A round rod between two local points (a rail, a chord). */
function rod(material: ObjectPartMaterial, a: Vec3, b: Vec3, radius: number): ObjectPart {
    const part = strut(material, a, b, radius * 2);
    return { ...part, shape: 'cylinder', axis: 'z' };
}

/** A prism over a ring of local points between two heights. */
function prism(
    material: ObjectPartMaterial,
    ring: readonly LocalPoint[],
    y0: number,
    y1: number,
    tag?: ObjectPart['tag']
): ObjectPart {
    const xs = ring.map((p) => p.x);
    const zs = ring.map((p) => p.z);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const z0 = Math.min(...zs);
    const z1 = Math.max(...zs);
    return {
        center: vec((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2),
        size: vec(x1 - x0, y1 - y0, z1 - z0),
        material,
        shape: 'extrusion',
        outline: ring.map((p) => ({ x: p.x, z: p.z })),
        ...(tag ? { tag } : {})
    };
}

/** The metal frame around a screen's picture, m. */
const screenFrame = (w: number, h: number): number => Math.min(0.06, w * 0.02, h * 0.04);

/**
 * The picture of a curved screen `w` x `h` m bent by `sagitta`: inside the frame, on the same circle as the backing
 * (its chord `width`, its ends `sagitta` in front of its center), `height` between the top and bottom frame bars
 * (centered: a picture reaching under the bars fights their faces in depth, CIN-15417).
 */
export function curvedScreenPicture(
    w: number,
    h: number,
    sagitta: number
): { width: number; height: number; sagitta: number } {
    const frame = screenFrame(w, h);
    const hx = w / 2;
    return {
        width: w - 2 * frame,
        height: h - 2 * frame,
        sagitta: curveDepthAt(hx - frame, hx, Math.min(sagitta, hx))
    };
}

/** Segments of an arc band (a curved screen's backing, bars and frame), enough to read round at any size. */
const CURVE_SEGMENTS = 24;

/**
 * A curved screen (CIN-15402): the flat screen's parts bent along the arc of `sagitta` over the width, its center
 * where the flat face is and its ends forward. Pieces across the width are arc bands (extrusions); the side bars
 * and side frames are boxes turned to the arc's end tangent, continuing it outward.
 */
function curvedScreenParts(w: number, d: number, h: number, sagitta: number, profile: ObjectProfile): ObjectPart[] {
    const frame = screenFrame(w, h);
    const hx = w / 2;
    const hz = d / 2;
    const s = Math.min(sagitta, hx);
    const band = (
        material: ObjectPartMaterial,
        [x0, x1]: [number, number],
        [y0, y1]: [number, number],
        [z0, z1]: [number, number]
    ): ObjectPart => {
        const xs = Array.from({ length: CURVE_SEGMENTS + 1 }, (_, i) => x0 + ((x1 - x0) * i) / CURVE_SEGMENTS);
        const front = xs.map((x) => ({ x, z: z1 + curveDepthAt(x, hx, s) }));
        const back = xs.map((x) => ({ x, z: z0 + curveDepthAt(x, hx, s) })).reverse();
        return prism(material, [...front, ...back], y0, y1);
    };
    // A box at one end (`side` -1 left, 1 right), `[t0, t1]` along the end tangent from the end (outward positive
    // on the right), `[z0, z1]` along the end normal as in the flat screen.
    const angle = curveEndAngle(hx, s);
    const atEnd = (
        material: ObjectPartMaterial,
        side: -1 | 1,
        [t0, t1]: [number, number],
        [y0, y1]: [number, number],
        [z0, z1]: [number, number]
    ): ObjectPart => {
        const phi = side * angle;
        const t = (t0 + t1) / 2;
        const n = (z0 + z1) / 2;
        return {
            center: vec(
                side * hx + Math.cos(phi) * t - Math.sin(phi) * n,
                (y0 + y1) / 2,
                s + Math.sin(phi) * t + Math.cos(phi) * n
            ),
            size: vec(t1 - t0, y1 - y0, z1 - z0),
            material,
            rotY: -phi
        };
    };
    const parts: ObjectPart[] = [
        band('dark', [-hx, hx], [0, h], [-hz, hz - 0.02]),
        atEnd('metal', -1, [0, frame], [0, h], [-hz, hz]),
        atEnd('metal', 1, [-frame, 0], [0, h], [-hz, hz]),
        band('metal', [-hx + frame, hx - frame], [h - frame, h], [-hz, hz]),
        band('metal', [-hx + frame, hx - frame], [0, frame], [-hz, hz]),
        {
            ...span('glow', [-hx + frame, hx - frame], [frame, h - frame], [hz - 0.03, hz - 0.01]),
            tag: 'screen-surface',
            curve: curvedScreenPicture(w, h, s).sagitta
        }
    ];
    if (!profile.bezel) return parts;
    const side = (key: 'left' | 'right' | 'top' | 'bottom'): number =>
        (profile.bezel?.[key] ?? profile.bezel?.width ?? 0) / MM;
    const [left, right, top, bottom] = [side('left'), side('right'), side('top'), side('bottom')];
    const z: [number, number] = [-hz, hz + profile.bezel.depth / MM];
    if (left > 0) parts.push(atEnd('body', -1, [-left, 0], [-bottom, h + top], z));
    if (right > 0) parts.push(atEnd('body', 1, [0, right], [-bottom, h + top], z));
    if (top > 0) parts.push(band('body', [-hx, hx], [h, h + top], z));
    if (bottom > 0) parts.push(band('body', [-hx, hx], [-bottom, 0], z));
    return parts;
}

function screenParts(w: number, d: number, h: number, profile?: ObjectProfile | null): ObjectPart[] {
    if (profile?.curve) return curvedScreenParts(w, d, h, profile.curve / MM, profile);
    const frame = screenFrame(w, h);
    const hx = w / 2;
    const hz = d / 2;
    const parts = [
        span('dark', [-hx, hx], [0, h], [-hz, hz - 0.02]),
        span('metal', [-hx, -hx + frame], [0, h], [-hz, hz]),
        span('metal', [hx - frame, hx], [0, h], [-hz, hz]),
        span('metal', [-hx + frame, hx - frame], [h - frame, h], [-hz, hz]),
        span('metal', [-hx + frame, hx - frame], [0, frame], [-hz, hz]),
        span('glow', [-hx + frame, hx - frame], [frame, h - frame], [hz - 0.03, hz - 0.01], { tag: 'screen-surface' })
    ];
    if (!profile?.bezel) return parts;
    // CIN-15200: a frame around the picture (the white proscenium of an old cinema), standing proud of the screen
    // toward the audience. It may reach below the screen's base, the screen hangs above the floor.
    // Each side falls back to `width`; 0 leaves that side unframed.
    const side = (key: 'left' | 'right' | 'top' | 'bottom'): number =>
        (profile.bezel?.[key] ?? profile.bezel?.width ?? 0) / MM;
    const [left, right, top, bottom] = [side('left'), side('right'), side('top'), side('bottom')];
    const front = hz + profile.bezel.depth / MM;
    const z: [number, number] = [-hz, front];
    const sides: ObjectPart[] = [];
    if (left > 0) sides.push(span('body', [-hx - left, -hx], [-bottom, h + top], z));
    if (right > 0) sides.push(span('body', [hx, hx + right], [-bottom, h + top], z));
    if (top > 0) sides.push(span('body', [-hx, hx], [h, h + top], z));
    if (bottom > 0) sides.push(span('body', [-hx, hx], [-bottom, 0], z));
    return [...parts, ...sides];
}

/**
 * How far a stage's body stands back from the dark band under its top edge, and the band from the top. Flush, the
 * band's faces were coplanar with the body's top, front and sides and z-fought into dark dashes along the edge; 3 mm
 * stays clear of the depth buffer's resolution across the room (about 1 mm at 30 m with the maps' 0.05 m near plane).
 */
const EDGE_LIP = 0.003;

function stageParts(w: number, d: number, h: number): ObjectPart[] {
    const hx = w / 2;
    const hz = d / 2;
    const stepDepth = Math.min(0.5, d * 0.2);
    const stepHeight = h * 0.45;
    const front = hz - 0.01;
    return [
        span('body', [-hx + EDGE_LIP, hx - EDGE_LIP], [0, h], [-hz, hz - stepDepth - EDGE_LIP]),
        span('body', [-hx, hx], [0, stepHeight], [hz - stepDepth - EDGE_LIP, front]),
        // The LED strip sits on the step's face, so the audience sees a glowing stage edge.
        span('glow', [-hx + 0.05, hx - 0.05], [stepHeight * 0.4, stepHeight * 0.4 + 0.03], [front - 0.002, hz]),
        span('dark', [-hx, hx], [h - 0.03, h - EDGE_LIP], [hz - stepDepth - 0.02, hz - stepDepth])
    ];
}

const GRILLE_LEVELS = [0.28, 0.5, 0.72];
const GRILLE_SLOT_HEIGHT = 0.035;
const SPOTLIGHT_PITCH = Math.PI / 6;

const centroidOf = (ring: readonly LocalPoint[]): LocalPoint => ({
    x: ring.reduce((sum, p) => sum + p.x, 0) / ring.length,
    z: ring.reduce((sum, p) => sum + p.z, 0) / ring.length
});

/**
 * A stage with a profile (CIN-15126): the outline extruded to the stage height, the steps around it as bands, and
 * the riser style on its outermost front face. An open or degenerate path falls back to the plain stage.
 */
function profiledStageParts(w: number, d: number, h: number, profile: ObjectProfile): ObjectPart[] {
    if (!profile.path.closed || profile.path.points.length < 3) return stageParts(w, d, h);
    const { bands, face } = stepModel(profile, h);
    const ring = profileRingLocal(profile);
    if (ring.length < 3) return stageParts(w, d, h);
    const parts: ObjectPart[] = [prism('body', offsetRingLocal(profile, -EDGE_LIP * 1000), 0, h)];
    for (const band of bands) parts.push(prism('body', band.ring, 0, band.top));
    // A dark ring just under the edge of the stage separates the top from the front face.
    parts.push(prism('dark', ring, h - 0.03, h - EDGE_LIP));
    if (!face || !profile.riser || profile.riser === 'plain') return parts;
    const center = centroidOf(ring);
    for (const run of face.runs) {
        for (let i = 1; i < run.length; i += 1) {
            const a = run[i - 1];
            const b = run[i];
            const length = Math.hypot(b.x - a.x, b.z - a.z);
            if (length < 0.05) continue;
            // Move the decoration a hair off the face, away from the stage center.
            let nx = -(b.z - a.z) / length;
            let nz = (b.x - a.x) / length;
            if (nx * ((a.x + b.x) / 2 - center.x) + nz * ((a.z + b.z) / 2 - center.z) < 0) {
                nx = -nx;
                nz = -nz;
            }
            const lift = (p: LocalPoint, y: number, out: number): Vec3 => vec(p.x + nx * out, y, p.z + nz * out);
            if (profile.riser === 'led') {
                parts.push(strut('glow', lift(a, face.top * 0.55, 0.006), lift(b, face.top * 0.55, 0.006), 0.02, 0.03));
                continue;
            }
            // Vent grille: three horizontal slots per piece of face, inset from the piece ends.
            for (const level of GRILLE_LEVELS) {
                const y = face.top * level;
                const from = { x: a.x + (b.x - a.x) * 0.12, z: a.z + (b.z - a.z) * 0.12 };
                const to = { x: a.x + (b.x - a.x) * 0.88, z: a.z + (b.z - a.z) * 0.88 };
                parts.push(strut('dark', lift(from, y, 0.008), lift(to, y, 0.008), 0.012, GRILLE_SLOT_HEIGHT));
            }
        }
    }
    return parts;
}

/** The profile path in local meters (a closed ring without a repeated last point). */
function profileRingLocal(profile: ObjectProfile, toleranceMm?: number): LocalPoint[] {
    return toLocalRing(profilePlanPoints(profile, toleranceMm).points);
}

/** Where the barrier's `thickness` comes from when its profile names none. */
const DEFAULT_BARRIER_THICKNESS_MM = 180;
const HANDRAIL_RADIUS = 0.03;
const BRASS_RADIUS = 0.034;
const GLASS_CAP_HEIGHT = 0.45;
const RAILING_POST_SPACING_MM = 1200;
const RAILING_POST_DIAMETER = 0.036;
const RAILING_TOP_RADIUS = 0.022;

const straightRun = (widthM: number): { x: number; y: number }[] => [
    { x: (-widthM / 2) * 1000, y: 0 },
    { x: (widthM / 2) * 1000, y: 0 }
];

/** Height the cap adds on top of the wall; it sits inside the object's stored height. */
function capHeight(cap: NonNullable<ObjectProfile['cap']>): number {
    if (cap === 'handrail') return HANDRAIL_RADIUS * 2;
    if (cap === 'brass') return BRASS_RADIUS * 2;
    if (cap === 'glass') return GLASS_CAP_HEIGHT;
    return 0;
}

/**
 * A guard wall: the path (a straight line of the footprint's width without a profile) thickened and extruded,
 * split around its openings, with a cap (handrail, brass rail or glass panel) and an LED strip. The object's
 * height is the total height: the wall is as tall as that minus the cap.
 */
function barrierParts(w: number, d: number, objectHeight: number, profile?: ObjectProfile | null): ObjectPart[] {
    const cap = profile?.cap ?? 'handrail';
    const led = profile?.led ?? 'none';
    const h = Math.max(0.05, objectHeight - capHeight(cap));
    const thicknessMm = profile?.thickness ?? (profile ? DEFAULT_BARRIER_THICKNESS_MM : Math.round(d * 1000));
    const runs = profile ? pathRuns(profile) : [straightRun(w)];
    const parts: ObjectPart[] = [];
    for (const run of runs) {
        const ring = wallRing(run, thicknessMm);
        if (!ring) continue;
        parts.push(prism('body', toLocalRing(ring), 0, h));
        const line = run.map(planToLocal);
        const half = thicknessMm / 2000;
        for (let i = 1; i < line.length; i += 1) {
            const a = line[i - 1];
            const b = line[i];
            const at = (p: LocalPoint, y: number): Vec3 => vec(p.x, y, p.z);
            if (cap === 'handrail') {
                parts.push(rod('metal', at(a, h + HANDRAIL_RADIUS), at(b, h + HANDRAIL_RADIUS), HANDRAIL_RADIUS));
            }
            if (cap === 'brass')
                parts.push(rod('brass', at(a, h + BRASS_RADIUS), at(b, h + BRASS_RADIUS), BRASS_RADIUS));
            if (cap === 'glass') {
                const y = h + GLASS_CAP_HEIGHT / 2;
                parts.push(strut('glass', at(a, y), at(b, y), 0.02, GLASS_CAP_HEIGHT));
            }
            if (led === 'none') continue;
            const length = Math.hypot(b.x - a.x, b.z - a.z);
            if (length < 1e-6) continue;
            const nx = -(b.z - a.z) / length;
            const nz = (b.x - a.x) / length;
            if (led === 'top') {
                parts.push(strut('glow', at(a, h + 0.004), at(b, h + 0.004), 0.03, 0.012));
                continue;
            }
            const y = led === 'face' ? h * 0.55 : 0.06;
            for (const side of [-1, 1]) {
                const out = half + 0.004;
                parts.push(
                    strut(
                        'glow',
                        vec(a.x + nx * out * side, y, a.z + nz * out * side),
                        vec(b.x + nx * out * side, y, b.z + nz * out * side),
                        0.02,
                        0.03
                    )
                );
            }
        }
    }
    return parts;
}

/**
 * The floor under an object as an offset from its base (CIN-15200): object-local plan mm in, meters out (negative
 * where the floor is lower than the base). A railing that crosses risers follows it.
 */
export type ObjectFloor = (local: PlanPoint) => number;

/** A railing post, or one side of a riser the railing crosses: plan-local mm and the floor there, m. */
type RailStation = { p: PlanPoint; floor: number };

/** Floor steps smaller than this (m) are not risers. */
const RAIL_STEP_MIN = 0.01;
/** How finely the floor under a railing is probed for risers, mm. */
const RAIL_PROBE_MM = 50;

/**
 * The stations of a railing run: the post positions with their floor and, where the floor jumps between two of
 * them, two stations at the riser (the floor before and after it) so the rails step like stairs.
 */
export function railStations(run: readonly PlanPoint[], floorAt?: ObjectFloor): RailStation[] {
    const posts = resampleRun(run, RAILING_POST_SPACING_MM);
    if (!floorAt) return posts.map((p) => ({ p, floor: 0 }));
    const out: RailStation[] = [{ p: posts[0], floor: floorAt(posts[0]) }];
    for (let i = 1; i < posts.length; i += 1) {
        const a = posts[i - 1];
        const b = posts[i];
        const probes = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / RAIL_PROBE_MM));
        let last = out[out.length - 1].floor;
        for (let k = 1; k < probes; k += 1) {
            const p = { x: a.x + ((b.x - a.x) * k) / probes, y: a.y + ((b.y - a.y) * k) / probes };
            const floor = floorAt(p);
            if (Math.abs(floor - last) < RAIL_STEP_MIN) continue;
            out.push({ p, floor: last }, { p, floor });
            last = floor;
        }
        const end = floorAt(b);
        if (Math.abs(end - last) >= RAIL_STEP_MIN) out.push({ p: b, floor: last });
        out.push({ p: b, floor: end });
    }
    return out;
}

const samePoint = (a: LocalPoint, b: LocalPoint | undefined): boolean => !!b && a.x === b.x && a.z === b.z;

/**
 * A railing: posts every 1.2 m with a top rail and a mid rail, along the path (or the footprint's width). With
 * `floorAt` it stands on the floor under each post and steps up and down at the risers it crosses.
 */
function railingParts(
    w: number,
    d: number,
    h: number,
    profile?: ObjectProfile | null,
    floorAt?: ObjectFloor
): ObjectPart[] {
    const rail: ObjectPartMaterial = profile?.cap === 'brass' ? 'brass' : 'metal';
    // Without a profile the end posts stand inside the footprint.
    const runs = profile ? pathRuns(profile) : [straightRun(Math.max(0.001, w - RAILING_POST_DIAMETER))];
    const parts: ObjectPart[] = [];
    for (const run of runs) {
        const stations = railStations(run, floorAt).map(({ p, floor }) => ({ at: planToLocal(p), floor }));
        stations.forEach(({ at: p, floor }, i) => {
            const previous = stations[i - 1];
            const riser = samePoint(p, previous?.at);
            // The two stations of a riser share one post, from the lower floor to the top of the upper rail.
            if (riser) {
                const from = Math.min(previous.floor, floor);
                const to = Math.max(previous.floor, floor) + h;
                parts.push(
                    cylinder(
                        rail,
                        vec(p.x, (from + to) / 2, p.z),
                        { x: RAILING_POST_DIAMETER, z: RAILING_POST_DIAMETER },
                        to - from
                    )
                );
            } else if (!samePoint(p, stations[i + 1]?.at)) {
                parts.push(
                    cylinder(
                        rail,
                        vec(p.x, floor + h / 2, p.z),
                        { x: RAILING_POST_DIAMETER, z: RAILING_POST_DIAMETER },
                        h
                    )
                );
            }
            if (!previous) return;
            const q = previous.at;
            parts.push(
                rod(
                    rail,
                    vec(q.x, previous.floor + h - RAILING_TOP_RADIUS, q.z),
                    vec(p.x, floor + h - RAILING_TOP_RADIUS, p.z),
                    RAILING_TOP_RADIUS
                )
            );
            if (riser) return;
            parts.push(rod(rail, vec(q.x, floor + h * 0.5, q.z), vec(p.x, floor + h * 0.5, p.z), 0.014));
            if (profile?.cap === 'glass') {
                const y = floor + 0.12 + (h * 0.8) / 2;
                parts.push(strut('glass', vec(q.x, y, q.z), vec(p.x, y, p.z), 0.015, h * 0.8));
            }
        });
    }
    return parts;
}

const COUNTER_TOP_M = 0.04;
/** The counter's top plate sits this far under its rim, so the cupholders stand a little proud of it. */
const COUNTER_RECESS_M = 0.006;
const COUNTER_CUP_DIAMETER = 0.1;
const COUNTER_GRILLE_LEVELS = [0.25, 0.5, 0.75];
const COUNTER_GRILLE_PITCH_MM = 500;
const COUNTER_GRILLE_LENGTH = 0.8;
/** How far the front face stands back from the outer edge of the grille slots, m. */
const COUNTER_FACE_INSET = 0.008;
/** Distance between cupholders along a counter, mm, when the profile names none. */
export const COUNTER_CUP_SPACING_MM = 900;

/** The profile's cupholder spacing, else the default. */
function counterCupSpacing(profile?: ObjectProfile | null): number {
    const spacing = profile?.cupholderSpacing;
    return typeof spacing === 'number' &&
        spacing >= CUPHOLDER_SPACING_RANGE.min &&
        spacing <= CUPHOLDER_SPACING_RANGE.max
        ? spacing
        : COUNTER_CUP_SPACING_MM;
}

/**
 * A counter along the front of a podium or a row of recliners: a dark ledge (straight, or a curved path following
 * the row) about 0.75 m high with a flat top, glowing cupholder rings set into the top at even spacing, and
 * ventilation grilles in the vertical face toward the audience.
 */
function counterParts(w: number, d: number, h: number, profile?: ObjectProfile | null): ObjectPart[] {
    const thicknessMm = profile?.thickness ?? Math.round(d * 1000);
    const runs = profile ? pathRuns(profile) : [straightRun(w)];
    const spacing = counterCupSpacing(profile);
    const bar = profile?.cupholders === false;
    const topBottom = h - COUNTER_TOP_M - COUNTER_RECESS_M;
    const parts: ObjectPart[] = [];
    for (const run of runs) {
        // The body is a hair thinner than the counter so the grille slots stand proud of the face, inside the footprint.
        const body = wallRing(run, thicknessMm - COUNTER_FACE_INSET * 2000);
        const top = wallRing(run, thicknessMm);
        if (!body || !top) continue;
        parts.push(prism('body', toLocalRing(body), 0, topBottom));
        parts.push(prism(bar ? 'body' : 'dark', toLocalRing(top), topBottom, h - COUNTER_RECESS_M));
        // A bar (CIN-15200, `cupholders: false`) is a plain counter: no cupholder rings, no grilles.
        if (bar) continue;
        for (const at of pointsAlong(run, spacing).map(planToLocal)) {
            parts.push(
                cylinder('dark', vec(at.x, h - COUNTER_RECESS_M / 2, at.z), { x: 0.07, z: 0.07 }, COUNTER_RECESS_M)
            );
            parts.push(
                cylinder(
                    'glow',
                    vec(at.x, h - COUNTER_RECESS_M + 0.0025, at.z),
                    { x: COUNTER_CUP_DIAMETER, z: COUNTER_CUP_DIAMETER },
                    0.005
                )
            );
        }
        // Grilles: short slots in three rows, along a line just inside the face toward the audience.
        // The slots are 12 mm thick and end flush with the counter's footprint: their center is 6 mm inside its edge.
        const face = towardAudience(run, thicknessMm / 2 - 6);
        const line = resampleRun(face, COUNTER_GRILLE_PITCH_MM).map(planToLocal);
        for (let i = 1; i < line.length; i += 1) {
            const a = line[i - 1];
            const b = line[i];
            const from = {
                x: a.x + (b.x - a.x) * ((1 - COUNTER_GRILLE_LENGTH) / 2),
                z: a.z + (b.z - a.z) * ((1 - COUNTER_GRILLE_LENGTH) / 2)
            };
            const to = {
                x: a.x + (b.x - a.x) * ((1 + COUNTER_GRILLE_LENGTH) / 2),
                z: a.z + (b.z - a.z) * ((1 + COUNTER_GRILLE_LENGTH) / 2)
            };
            for (const level of COUNTER_GRILLE_LEVELS) {
                const y = topBottom * level;
                parts.push(strut('metal', vec(from.x, y, from.z), vec(to.x, y, to.z), 0.012, 0.03));
            }
        }
    }
    return parts;
}

/**
 * A stage spotlight: a wall plate, an arm and a can tilted toward the front with a glowing lens. Like the
 * projector, the lens looks toward -z (at the screen), so `frontOf('spotlight')` is 1.
 */
function spotlightParts(w: number, d: number, h: number): ObjectPart[] {
    const hz = d / 2;
    const radius = 0.2 * Math.min(w, h, d);
    const canLength = Math.min(0.75 * d, 1.2 * h);
    const pitch = SPOTLIGHT_PITCH;
    const dir = { y: -Math.sin(pitch), z: -Math.cos(pitch) };
    // The can's lowest point stands on y = 0 (its bounding box is what the object's base is measured by).
    const halfHeight = (canLength / 2) * Math.sin(pitch) + radius * Math.cos(pitch);
    const center = vec(0, halfHeight, 0);
    const back = vec(0, center.y - (dir.y * canLength) / 2, center.z - (dir.z * canLength) / 2);
    const front = vec(0, center.y + (dir.y * (canLength - 0.02)) / 2, center.z + (dir.z * (canLength - 0.02)) / 2);
    const lensEnd = vec(0, front.y + dir.y * 0.02, front.z + dir.z * 0.02);
    return groundParts([
        span('metal', [-w * 0.4, w * 0.4], [Math.max(halfHeight * 2, h * 0.55), h], [hz - 0.02, hz]),
        span('metal', [-0.02, 0.02], [halfHeight, h * 0.9], [0, hz - 0.02]),
        rod('dark', back, front, radius),
        rod('glow', front, lensEnd, radius * 0.85)
    ]);
}

/** The parts moved up or down so the lowest one stands on y = 0. */
function groundParts(parts: ObjectPart[]): ObjectPart[] {
    const lowest = Math.min(...parts.map((part) => partExtents(part).min.y));
    return parts.map((part) => ({ ...part, center: vec(part.center.x, part.center.y - lowest, part.center.z) }));
}

/** A lattice truss along x: four chords and a zig-zag web on the front and back faces. */
function trussParts(w: number, d: number, h: number): ObjectPart[] {
    const chord = Math.max(0.012, Math.min(d, h) * 0.07);
    const hx = w / 2;
    const lo = chord;
    const hi = h - chord;
    const parts: ObjectPart[] = [];
    for (const y of [lo, hi]) {
        for (const z of [-d / 2 + chord, d / 2 - chord]) parts.push(rod('metal', vec(-hx, y, z), vec(hx, y, z), chord));
    }
    const cells = Math.max(1, Math.round(w / Math.max(h, 0.1)));
    const pitch = w / cells;
    for (const z of [-d / 2 + chord, d / 2 - chord]) {
        for (let i = 0; i < cells; i += 1) {
            const x0 = -hx + i * pitch + chord;
            const x1 = -hx + (i + 1) * pitch - chord;
            const rising = i % 2 === 0;
            parts.push(strut('metal', vec(x0, rising ? lo : hi, z), vec(x1, rising ? hi : lo, z), chord * 0.6));
        }
    }
    return parts;
}

/** A `w` x `d` rectangle with corners rounded by `r`, centered on the origin. */
function roundedRectRing(w: number, d: number, r: number): LocalPoint[] {
    const ring: LocalPoint[] = [];
    const corners: [number, number, number][] = [
        [w / 2 - r, d / 2 - r, 0],
        [-w / 2 + r, d / 2 - r, Math.PI / 2],
        [-w / 2 + r, -d / 2 + r, Math.PI],
        [w / 2 - r, -d / 2 + r, (3 * Math.PI) / 2]
    ];
    for (const [cx, cz, start] of corners) {
        for (let i = 0; i <= 4; i += 1) {
            const a = start + (i / 4) * (Math.PI / 2);
            ring.push({ x: cx + r * Math.cos(a), z: cz + r * Math.sin(a) });
        }
    }
    return ring;
}

/**
 * A footstool (the padded ottoman in front of a lounge recliner): a square foot plate, a short post and a cushion
 * with rounded corners whose top swells a little above its sides. The cushion wears the object's own material.
 */
function footstoolParts(w: number, d: number, h: number): ObjectPart[] {
    const cushion = Math.min(0.12, h * 0.35);
    const corner = Math.min(w, d) * 0.22;
    const crown = Math.min(0.025, cushion * 0.3);
    const inset = Math.min(0.015, corner / 2);
    const plate = Math.min(w, d) * 0.5;
    const post = Math.min(0.08, Math.min(w, d) * 0.2);
    const cushionBottom = h - cushion;
    return [
        span('metal', [-plate / 2, plate / 2], [0, 0.015], [-plate / 2, plate / 2]),
        cylinder(
            'dark',
            vec(0, (0.015 + cushionBottom) / 2, 0),
            { x: post, z: post },
            Math.max(0.001, cushionBottom - 0.015)
        ),
        prism('body', roundedRectRing(w, d, corner), cushionBottom, h - crown, 'rounded'),
        prism('body', roundedRectRing(w - 2 * inset, d - 2 * inset, corner - inset), h - crown - 0.005, h, 'rounded')
    ];
}

/** Riser and tread of the stair, N steps derived from the total rise. */
function stairStepCount(height: number): number {
    return Math.min(40, Math.max(1, Math.round(height / 0.17)));
}

function stairsParts(w: number, d: number, h: number, profile?: ObjectProfile | null): ObjectPart[] {
    const n = stairStepCount(h);
    const riser = h / n;
    const tread = d / n;
    const hx = w / 2;
    const hz = d / 2;
    const parts: ObjectPart[] = [];
    for (let i = 0; i < n; i++) {
        const top = (i + 1) * riser;
        const zFront = hz - i * tread;
        const zBack = zFront - tread;
        parts.push(span('body', [-hx, hx], [0, top], [zBack, zFront - 0.008]));
        parts.push(span('glow', [-hx + 0.04, hx - 0.04], [top - 0.035, top - 0.012], [zFront - 0.01, zFront]));
    }
    // An explicit rails choice is honored at any width; the default only fits wide stairs.
    if (w >= 0.9 || profile?.rails) {
        const railH = 0.85;
        const rail = 0.04;
        const zFoot = hz - 0.04;
        const zTop = -hz + 0.04;
        const yFoot = riser + railH;
        const yTop = h + railH;
        const dy = yTop - yFoot;
        const dz = zTop - zFoot;
        const length = Math.hypot(dy, dz);
        // Climbing runs toward -z (local), so the climber's right hand is +x.
        const rails = profile?.rails ?? 'both';
        const sides = rails === 'both' ? [-1, 1] : rails === 'left' ? [-1] : rails === 'right' ? [1] : [];
        for (const side of sides) {
            const x = side * (hx - 0.04);
            parts.push({
                center: vec(x, (yFoot + yTop) / 2, (zFoot + zTop) / 2),
                size: vec(rail, rail, length),
                material: 'metal',
                tilt: Math.atan2(dy, -dz)
            });
            const posts = Math.max(2, Math.ceil(n / 3) + 1);
            for (let p = 0; p < posts; p++) {
                const t = p / (posts - 1);
                const y = yFoot + dy * t;
                parts.push(cylinder('metal', vec(x, y / 2, zFoot + dz * t), { x: 0.035, z: 0.035 }, y));
            }
        }
    }
    return parts;
}

function pillarParts(w: number, d: number, h: number, profile?: ObjectProfile | null): ObjectPart[] {
    const baseH = Math.min(0.14, h * 0.05);
    const capH = Math.min(0.18, h * 0.06);
    const shaft = 0.84;
    // CIN-15200: a closed profile is the pillar's cross-section (round, square, any outline).
    const ring = profile?.path.closed ? profileRingLocal(profile) : [];
    if (ring.length >= 3) {
        const inner = ring.map((p) => ({ x: p.x * shaft, z: p.z * shaft }));
        return [
            prism('metal', ring, 0, baseH),
            prism('body', inner, baseH, h - capH),
            prism('metal', ring, h - capH, h)
        ];
    }
    const round = isRoundFootprint(w, d);
    if (round) {
        return [
            cylinder('metal', vec(0, baseH / 2, 0), { x: w, z: d }, baseH),
            cylinder(
                'body',
                vec(0, baseH + (h - baseH - capH) / 2, 0),
                { x: w * shaft, z: d * shaft },
                h - baseH - capH
            ),
            cylinder('metal', vec(0, h - capH / 2, 0), { x: w, z: d }, capH)
        ];
    }
    return [
        span('metal', [-w / 2, w / 2], [0, baseH], [-d / 2, d / 2]),
        span('body', [(-w * shaft) / 2, (w * shaft) / 2], [baseH, h - capH], [(-d * shaft) / 2, (d * shaft) / 2]),
        span('metal', [-w / 2, w / 2], [h - capH, h], [-d / 2, d / 2])
    ];
}

/** A wall along a curved path (a niche, a rounded partition): the path thickened by the footprint's depth and extruded. */
function profiledWallParts(w: number, d: number, h: number, profile: ObjectProfile): ObjectPart[] {
    const skirting = Math.min(0.1, h * 0.05);
    const thicknessMm = profile.thickness ?? Math.round(d * 1000);
    const parts: ObjectPart[] = [];
    for (const run of pathRuns(profile)) {
        const ring = wallRing(run, thicknessMm);
        if (!ring) continue;
        parts.push(prism('body', toLocalRing(ring), skirting, h));
        parts.push(prism('dark', toLocalRing(ring), 0, skirting));
    }
    return parts.length > 0 ? parts : wallParts(w, d, h);
}

function wallParts(w: number, d: number, h: number): ObjectPart[] {
    const skirting = Math.min(0.1, h * 0.05);
    return [
        span('body', [-w / 2, w / 2], [skirting, h], [-d / 2 + 0.004, d / 2 - 0.004]),
        span('dark', [-w / 2, w / 2], [0, skirting], [-d / 2, d / 2])
    ];
}

function doorParts(w: number, d: number, h: number, signHeight: number): ObjectPart[] {
    const hx = w / 2;
    const hz = d / 2;
    const jamb = Math.min(0.07, w * 0.08);
    const leafTop = h - signHeight;
    const leafThick = Math.min(0.045, d * 0.4);
    const handleOut = Math.min(0.03, (hz - leafThick / 2) / 1.5);
    const handleX = hx - jamb - 0.09;
    const parts: ObjectPart[] = [
        span('metal', [-hx, -hx + jamb], [0, leafTop], [-hz, hz]),
        span('metal', [hx - jamb, hx], [0, leafTop], [-hz, hz]),
        span('metal', [-hx + jamb, hx - jamb], [leafTop - jamb, leafTop], [-hz, hz]),
        span('body', [-hx + jamb, hx - jamb], [0, leafTop - jamb - 0.004], [-leafThick / 2, leafThick / 2])
    ];
    for (const side of [-1, 1]) {
        const zc = side * (leafThick / 2 + handleOut / 2);
        parts.push(
            span('metal', [handleX - 0.07, handleX + 0.07], [0.98, 1.005], [zc - handleOut / 2, zc + handleOut / 2])
        );
    }
    return parts;
}

const EXIT_SIGN_HEIGHT = 0.3;

function emergencyExitParts(w: number, d: number, h: number): ObjectPart[] {
    const signH = Math.min(EXIT_SIGN_HEIGHT, h * 0.2);
    const signW = Math.min(0.6, w * 0.8);
    const signD = Math.min(0.06, d * 0.5);
    return [
        ...doorParts(w, d, h, signH),
        span('glow', [-signW / 2, signW / 2], [h - signH + 0.04, h - 0.01], [-signD / 2, signD / 2], {
            tag: 'exit-sign'
        })
    ];
}

function speakerParts(w: number, d: number, h: number): ObjectPart[] {
    const hx = w / 2;
    const hz = d / 2;
    const woofer = Math.min(w, h) * 0.58;
    const tweeter = Math.min(w, h) * 0.2;
    const cone = 0.03;
    return [
        span('dark', [-hx, hx], [0, h], [-hz, hz - cone]),
        cylinder('metal', vec(0, h * 0.34, hz - cone * 0.65), { x: woofer, z: woofer }, cone * 0.7, 'z'),
        cylinder('dark', vec(0, h * 0.34, hz - cone / 2), { x: woofer * 0.7, z: woofer * 0.7 }, cone, 'z'),
        cylinder('metal', vec(0, h * 0.78, hz - cone / 2), { x: tweeter, z: tweeter }, cone, 'z')
    ];
}

/** Local z and y of the lens (meters), where the light cone starts. */
export function projectorLens(d: number, h: number): { y: number; z: number } {
    return { y: h * 0.45, z: -d / 2 };
}

/** Diameter of the lens barrel (meters). */
export function projectorLensDiameter(w: number, h: number): number {
    return Math.min(w * 0.42, h * 0.7);
}

function projectorParts(w: number, d: number, h: number): ObjectPart[] {
    const hx = w / 2;
    const hz = d / 2;
    const barrel = Math.min(0.1, d * 0.12);
    const glassDepth = 0.02;
    const lens = projectorLensDiameter(w, h);
    const bracket = Math.min(0.05, h * 0.1);
    const lensY = projectorLens(d, h).y;
    return [
        span('body', [-hx, hx], [0, h - bracket], [-hz + barrel + glassDepth, hz]),
        span('dark', [-hx * 0.3, hx * 0.3], [h - bracket, h], [-hz * 0.4, hz * 0.4]),
        cylinder('metal', vec(0, lensY, -hz + glassDepth + barrel / 2), { x: lens, z: lens }, barrel, 'z'),
        cylinder('glass', vec(0, lensY, -hz + glassDepth / 2), { x: lens * 0.82, z: lens * 0.82 }, glassDepth, 'z'),
        span('dark', [hx * 0.45, hx * 0.9], [h * 0.55, h * 0.8], [hz - 0.004, hz]),
        span(
            'glow',
            [-hx * 0.8, -hx * 0.7],
            [h * 0.12, h * 0.2],
            [-hz + barrel + glassDepth - 0.002, -hz + barrel + glassDepth + 0.006]
        )
    ];
}

function tableParts(w: number, d: number, h: number, profile?: ObjectProfile | null): ObjectPart[] {
    const top = 0.03;
    const lamp = 0.06;
    const topY = h - lamp - top;
    // CIN-15200: a closed profile is the table top (circle, oval, rectangle, rounded rectangle, any outline). A top
    // with a curved edge stands on a pedestal, a straight-edged one on four legs.
    const ring = profile?.path.closed ? profileRingLocal(profile, 3) : [];
    if (ring.length >= 3) {
        const curved = profile!.path.points.some((p) => (p.bulge ?? 0) !== 0);
        const xs = ring.map((p) => p.x);
        const zs = ring.map((p) => p.z);
        const hw = (Math.max(...xs) - Math.min(...xs)) / 2;
        const hd = (Math.max(...zs) - Math.min(...zs)) / 2;
        const base = curved
            ? [
                  cylinder('metal', vec(0, 0.015, 0), { x: Math.min(hw, 0.4), z: Math.min(hd, 0.4) }, 0.03),
                  cylinder('metal', vec(0, 0.03 + (topY - 0.03) / 2, 0), { x: 0.06, z: 0.06 }, topY - 0.03)
              ]
            : tableLegs(hw * 2, hd * 2, topY);
        return [
            ...base,
            prism('body', ring, topY, topY + top, 'rounded'),
            cylinder('glow', vec(0, h - lamp / 2, 0), { x: 0.06, z: 0.06 }, lamp)
        ];
    }
    if (isRoundFootprint(w, d)) {
        return [
            cylinder('metal', vec(0, 0.015, 0), { x: w * 0.5, z: d * 0.5 }, 0.03),
            cylinder('metal', vec(0, 0.03 + (topY - 0.03) / 2, 0), { x: 0.06, z: 0.06 }, topY - 0.03),
            cylinder('body', vec(0, topY + top / 2, 0), { x: w, z: d }, top),
            cylinder('glow', vec(0, h - lamp / 2, 0), { x: 0.06, z: 0.06 }, lamp)
        ];
    }
    return [
        ...tableLegs(w, d, topY),
        prism('body', rectRing(w, d), topY, topY + top, 'rounded'),
        cylinder('glow', vec(0, h - lamp / 2, 0), { x: 0.06, z: 0.06 }, lamp)
    ];
}

/** The corners of a `w` x `d` rectangle centered on the origin. */
function rectRing(w: number, d: number): LocalPoint[] {
    return [
        { x: -w / 2, z: -d / 2 },
        { x: w / 2, z: -d / 2 },
        { x: w / 2, z: d / 2 },
        { x: -w / 2, z: d / 2 }
    ];
}

/** Four square legs inset from the corners of a `w` x `d` top. */
function tableLegs(w: number, d: number, topY: number): ObjectPart[] {
    const leg = 0.04;
    const inset = 0.06;
    const legs: ObjectPart[] = [];
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const cx = sx * (w / 2 - inset - leg / 2);
            const cz = sz * (d / 2 - inset - leg / 2);
            legs.push(span('metal', [cx - leg / 2, cx + leg / 2], [0, topY], [cz - leg / 2, cz + leg / 2]));
        }
    }
    return legs;
}

function otherParts(w: number, d: number, h: number): ObjectPart[] {
    return [span('body', [-w / 2, w / 2], [0, h], [-d / 2, d / 2], { tag: 'rounded' })];
}

/**
 * Parts of an object of the given kind and size (mm), in local meters. A `profile` (CIN-15126) swaps the box for
 * a path: a round or polygonal stage with steps and a riser style, a curved barrier, railing or wall, the top of a
 * table or the cross-section of a pillar (CIN-15200); on a screen it only carries the bezel.
 */
export function objectParts(
    kind: RoomObjectKind,
    size: ObjectSize,
    profile?: ObjectProfile | null,
    floorAt?: ObjectFloor
): ObjectPart[] {
    const w = Math.max(size.width, 1) / MM;
    const d = Math.max(size.depth, 1) / MM;
    const h = Math.max(size.height, 1) / MM;
    switch (kind) {
        case 'screen':
            return screenParts(w, d, h, profile);
        case 'stage':
            return profile ? profiledStageParts(w, d, h, profile) : stageParts(w, d, h);
        case 'stairs':
            return stairsParts(w, d, h, profile);
        case 'pillar':
            return pillarParts(w, d, h, profile);
        case 'wall':
            return profile && profile.path.points.length >= 2
                ? profiledWallParts(w, d, h, profile)
                : wallParts(w, d, h);
        case 'barrier':
            return barrierParts(w, d, h, profile);
        case 'counter':
            return counterParts(w, d, h, profile);
        case 'railing':
            return railingParts(w, d, h, profile, floorAt);
        case 'spotlight':
            return spotlightParts(w, d, h);
        case 'truss':
            return trussParts(w, d, h);
        case 'footstool':
            return footstoolParts(w, d, h);
        case 'door':
            return doorParts(w, d, h, 0);
        case 'emergency_exit':
            return emergencyExitParts(w, d, h);
        case 'speaker':
            return speakerParts(w, d, h);
        case 'projector':
            return projectorParts(w, d, h);
        case 'table':
            return tableParts(w, d, h, profile);
        case 'other':
        default:
            return otherParts(w, d, h);
    }
}

/** Axis-aligned bounds of one part, honoring the tilt and heading (`tilt`, then `rotY`). */
export function partExtents(part: ObjectPart): { min: Vec3; max: Vec3 } {
    if (part.rotY) {
        const cosT = Math.cos(part.tilt ?? 0);
        const sinT = Math.sin(part.tilt ?? 0);
        const cosY = Math.cos(part.rotY);
        const sinY = Math.sin(part.rotY);
        const min = vec(Infinity, Infinity, Infinity);
        const max = vec(-Infinity, -Infinity, -Infinity);
        for (const sx of [-1, 1]) {
            for (const sy of [-1, 1]) {
                for (const sz of [-1, 1]) {
                    const x = (sx * part.size.x) / 2;
                    const y0 = (sy * part.size.y) / 2;
                    const z0 = (sz * part.size.z) / 2;
                    // Rx(tilt) then Ry(rotY).
                    const y = y0 * cosT - z0 * sinT;
                    const z1 = y0 * sinT + z0 * cosT;
                    const rx = x * cosY + z1 * sinY;
                    const rz = -x * sinY + z1 * cosY;
                    min.x = Math.min(min.x, part.center.x + rx);
                    max.x = Math.max(max.x, part.center.x + rx);
                    min.y = Math.min(min.y, part.center.y + y);
                    max.y = Math.max(max.y, part.center.y + y);
                    min.z = Math.min(min.z, part.center.z + rz);
                    max.z = Math.max(max.z, part.center.z + rz);
                }
            }
        }
        return { min, max };
    }
    let hy = part.size.y / 2;
    let hz = part.size.z / 2;
    if (part.tilt) {
        const c = Math.abs(Math.cos(part.tilt));
        const s = Math.abs(Math.sin(part.tilt));
        [hy, hz] = [c * hy + s * hz, s * hy + c * hz];
    }
    const hx = part.size.x / 2;
    return {
        min: vec(part.center.x - hx, part.center.y - hy, part.center.z - hz),
        max: vec(part.center.x + hx, part.center.y + hy, part.center.z + hz)
    };
}

/** Bounds of all parts, which the selection outline wraps (stair rails may exceed the nominal height). */
export function objectBounds(parts: ObjectPart[]): { min: Vec3; max: Vec3 } {
    const min = vec(Infinity, Infinity, Infinity);
    const max = vec(-Infinity, -Infinity, -Infinity);
    for (const part of parts) {
        const e = partExtents(part);
        min.x = Math.min(min.x, e.min.x);
        min.y = Math.min(min.y, e.min.y);
        min.z = Math.min(min.z, e.min.z);
        max.x = Math.max(max.x, e.max.x);
        max.y = Math.max(max.y, e.max.y);
        max.z = Math.max(max.z, e.max.z);
    }
    return { min, max };
}
